<template>
  <AddToCartButton
    v-if="isAllowedToAddToCart"
    :button-text="buttonText"
    :display-text="displayText"
    :display-icon="displayIcon"
    :disabled="isDisabled"
    :variant="variant"
    :button-title="buttonLabel"
    class="add-to-cart-button-container"
    @add-to-cart="prepareAddOfferToCart"
  >
    <template #badge>
      <CountBadge
        v-if="quantity"
        v-show="hasQuantityBadge"
        :value="quantity"
        class="add-to-cart-button-container__count"
      />
    </template>
  </AddToCartButton>
</template>

<script setup lang="ts">
import { useRoute } from '#imports';
import { storeToRefs } from 'pinia';

import { AddToCartButton, type LinkButtonVariant } from '@i22team/td-longtail-vue-components';
import CountBadge from '~/components/count-badge.vue';
import { isAllowedToAddToCart as isAllowedToAddToCartMagentaMoments } from '~/lib/magenta-moments';
import { useCartStore } from '~/stores/cart';
import { getBundleArticleNumbers, isManualBundle } from '~/lib/build-bundle-from-products';
import {
  addToCart,
  type AddToCartItem,
  getAddToCartItemFromOffer,
  getAddToCartItemFromStore,
  isAdding,
} from '~/helpers/add-to-cart';

export type AddToCartButtonContainerProps = {
  displayIcon?: boolean;
  displayText?: boolean;
  displayCartQuantity?: boolean;
  offer?: Offer | ListOffer | OfferProductAlternative | ListOfferProductAlternative;
  variant?: LinkButtonVariant;
  buttonText?: string;
  productName?: string;
  quantity?: number;
};

const props = withDefaults(defineProps<AddToCartButtonContainerProps>(), {
  displayIcon: true,
  displayText: true,
  buttonText: 'In den Warenkorb',
  productName: undefined,
  variant: 'primary',
  offer: undefined,
  quantity: 1,
});

const emits = defineEmits<{
  (e: 'error'): void;
  (e: 'added', items: AddToCartItem[]): void;
}>();

const route = useRoute();
const cartStore = useCartStore();

const { cartItems } = storeToRefs(cartStore);

const articleNumber = computed(() => props.offer?.product?.articleNumber);

const isAllowedToAddToCart = computed(() =>
  isAllowedToAddToCartMagentaMoments(articleNumber.value, route.path)
);

const cartItem = computed(() => {
  if (!articleNumber.value) return undefined;
  return cartItems.value.find((item) => item.articleNumber === articleNumber.value);
});

const cartQuantity = computed(() => cartItem.value?.quantity || 0);

const hasQuantityBadge = computed(() => props.displayCartQuantity && cartQuantity.value > 0);

const maxQuantity = computed(
  () =>
    cartItem.value?.maximumAvailable ||
    (props.offer && 'maxOrderableAmount' in props.offer && props.offer.maxOrderableAmount) ||
    Infinity
);

const stockQuantity = computed(() => props.offer?.product?.availability?.stock || 0);

const stockOrMaxQuantity = computed(() =>
  maxQuantity.value < stockQuantity.value ? maxQuantity.value : stockQuantity.value
);

const orderableQuantity = computed(() => {
  // max quantity available - currently in cart - quantity to be put in the cart
  const quantity = stockOrMaxQuantity.value - cartQuantity.value - (props.quantity - 1);

  return quantity >= 0 ? quantity : 0;
});

const inStock = computed(() => orderableQuantity.value > 0);

const buttonTitle = computed(() => {
  if (!inStock.value) {
    return `${props.productName || 'Dieser Artikel'} ist aktuell leider nicht verfügbar`;
  }

  if (props.quantity === 1) {
    return `${props.productName || 'Artikel'} in den Warenkorb legen`;
  }

  return `${props.quantity} ${props.productName || 'Artikel'} in den Warenkorb legen`;
});

const isDisabled = computed(() => {
  if (!isAllowedToAddToCart.value) return true;
  if (isAdding.value) return true;
  return !inStock.value;
});

const buttonLabel = computed(() => {
  // Disabled + Text: The label will trigger an error.
  if (props.displayText && isDisabled.value) return '';
  return buttonTitle.value;
});

const addToCartData = computed(() => {
  if (!articleNumber.value) return undefined;
  if (isManualBundle(articleNumber.value)) {
    return getBundleArticleNumbers(props.offer as ListOffer).map((mappedArticleNumber) => ({
      quantity: 1,
      articleNumber: mappedArticleNumber,
      name: props.offer?.product?.name || props.productName || 'Artikel',
    }));
  }
  return [
    {
      articleNumber: articleNumber.value,
      quantity: props.quantity || 1,
      name: props.offer?.product?.name || props.productName || 'Artikel',
    },
  ];
});

const prepareAddOfferToCart = async () => {
  if (isDisabled.value || !addToCartData.value || !articleNumber.value || !props.offer) return;

  if (await addToCart([props.offer])) {
    const item: AddToCartItem =
      getAddToCartItemFromStore(articleNumber.value, cartItems.value) ||
      getAddToCartItemFromOffer(props.offer) ||
      addToCartData.value[0];

    if (item) {
      emits('added', [item]);
    }
  }
};

defineExpose({
  hasQuantityBadge,
  buttonTitle,
  prepareAddOfferToCart,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.add-to-cart-button-container {
  &__count {
    overflow: visible;
    position: absolute;
    top: -10px;
    right: -10px;
  }
}
</style>
